import { RegisterScreen } from '@/components/RegisterScreen';
import { StatusPill, formatDate } from '@/components/live';
import { usePickers } from '@/hooks/usePickers';
import { PARTY_ROLES, optionLabel } from '@/lib/practiceLists';

/**
 * Party engagements (owner, 2026-09-27): which plots and sites a party works on, in which role,
 * between which dates. This is the Relationship leg of Actor + Role + Place, and the record every
 * party page hangs its activity on: a nursery's batches, a transporter's consignments, a
 * processor's runs all cite the engagement they were done under.
 *
 * The role is granted to the party on save if it does not hold it yet (migration 310000), so
 * engaging a company as a transporter IS onboarding it as one.
 */
interface EngagementRow {
  id: string;
  engagement_code: string | null;
  party_id: string;
  party_name: string;
  party_code: string | null;
  party_category: string;
  role_type: string;
  scope_kind: 'organisation' | 'plots' | 'sites';
  plot_ids: string[];
  site_ids: string[];
  plot_codes: string | null;
  site_names: string | null;
  effective_from: string;
  effective_to: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

const SCOPES = [
  { id: 'organisation', label: 'Whole organisation' },
  { id: 'plots', label: 'Named plots' },
  { id: 'sites', label: 'Named sites' },
];
const STATUSES = [{ id: 'active', label: 'Active' }, { id: 'inactive', label: 'Inactive' }, { id: 'ended', label: 'Ended' }];

export default function EngagementsScreen() {
  const pick = usePickers(['parties', 'plots', 'sites']);
  const today = new Date();

  return (
    <RegisterScreen<EngagementRow>
      addLabel="Engage a party"
      columns={[
        { key: 'engagement_code', label: 'Ref', render: (row) => <strong>{row.engagement_code ?? '--'}</strong> },
        { key: 'party_name', label: 'Party', render: (row) => <><strong>{row.party_name}</strong><small className="muted">{row.party_code ?? ''}</small></> },
        { key: 'role_type', label: 'As', render: (row) => optionLabel(PARTY_ROLES, row.role_type) },
        { key: 'scope_kind', label: 'Where', render: (row) => row.scope_kind === 'organisation' ? 'Whole organisation'
          : row.scope_kind === 'plots' ? (row.plot_codes ?? '--') : (row.site_names ?? '--') },
        { key: 'effective_from', label: 'From', render: (row) => formatDate(row.effective_from) },
        { key: 'effective_to', label: 'Until', render: (row) => (row.effective_to ? formatDate(row.effective_to) : 'Open') },
        { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
      ]}
      defaults={{ scope_kind: 'organisation', status: 'active', plot_ids: [], site_ids: [], effective_from: today.toISOString().slice(0, 10) }}
      exportName="party-engagements"
      fields={[
        { key: 'party_id', label: 'Party', type: 'select', options: pick.parties, required: true,
          hint: 'A person or company already registered. Register a new one from Contractors, Stakeholders or the farmer register first.' },
        { key: 'role_type', label: 'Engaged as', type: 'select', options: PARTY_ROLES, required: true,
          hint: 'The role is added to the party if it does not hold it yet.' },
        { key: 'scope_kind', label: 'Where', type: 'select', options: SCOPES, required: true },
        { key: 'plot_ids', label: 'Plots', type: 'multi', options: pick.plots, showIf: (form) => form.scope_kind === 'plots',
          required: (form) => form.scope_kind === 'plots' },
        { key: 'site_ids', label: 'Sites', type: 'multi', options: pick.sites, showIf: (form) => form.scope_kind === 'sites',
          required: (form) => form.scope_kind === 'sites' },
        { key: 'effective_from', label: 'From', type: 'date', required: true },
        { key: 'effective_to', label: 'Until', type: 'date', hint: 'Leave blank while the engagement is open.' },
        { key: 'status', label: 'Status', type: 'select', options: STATUSES, required: true },
        { key: 'notes', label: 'Notes', type: 'textarea', wide: true },
      ]}
      recordLabel={(row) => `${row.party_name} as ${optionLabel(PARTY_ROLES, row.role_type)}`}
      recordNoun="engagement"
      screenKey="party.register"
      searchColumns={['party_name', 'party_code', 'engagement_code', 'plot_codes', 'site_names']}
      stats={[
        { key: 'active', label: 'Active', tone: 'ok', match: (row) => row.status === 'active' },
        { key: 'plots', label: 'On named plots', tone: 'info', match: (row) => row.scope_kind === 'plots' },
        { key: 'sites', label: 'On named sites', tone: 'info', match: (row) => row.scope_kind === 'sites' },
        { key: 'ending', label: 'Ending in 30 days', tone: 'warn', match: (row) => Boolean(row.effective_to)
          && row.status === 'active' && (new Date(row.effective_to!).getTime() - today.getTime()) < 30 * 86_400_000 },
      ]}
      subtitle="Which plots and sites each party works on, in which role, and for how long."
      table="party.party_engagements"
      title="Party engagements"
      validate={(form) => ({
        effective_to: form.effective_to && form.effective_from && String(form.effective_to) < String(form.effective_from)
          ? 'The end is before the start.' : undefined,
      })}
      view="party_engagements"
    />
  );
}
